import { getGaiaobjectName } from "components/gaiaobject-name/GaiaobjectName";
import { generateDynamicAttributesDataMap } from "utils/dynamicAttributesUtils";
import { CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME } from "models/generateCustomAttributesSchema";

type CategoryMapping = {
  plural: string;
  singular: string;
};

const CATEGORY_MAPPINGS: Record<string, CategoryMapping> = {
  L: { plural: "litigations", singular: "litigation" },
  N: { plural: "notices", singular: "notice" },
  default: { plural: "audits", singular: "audit" },
};

export const getPolyTypeById = (shortId: string): string => {
  if (!shortId) return "";
  const prefix = shortId.charAt(0);
  return (CATEGORY_MAPPINGS[prefix] || CATEGORY_MAPPINGS.default).plural;
};

export const getCategoryById = (shortId: string): string => {
  if (!shortId) return "";
  const prefix = shortId.charAt(0);
  return (CATEGORY_MAPPINGS[prefix] || CATEGORY_MAPPINGS.default).singular;
};

export const getGaiaObjectRoute = (gaiaObjectId: string): string => {
  if (!gaiaObjectId) return "#";

  if (gaiaObjectId.startsWith("N")) {
    return `/notices/${gaiaObjectId}`;
  } else if (gaiaObjectId.startsWith("L")) {
    return `/litigations/${gaiaObjectId}`;
  } else if (gaiaObjectId.startsWith("audit")) {
    return `/audit/${gaiaObjectId}`;
  }
  return "#";
};

export const getCategoryTitle = (parentId: string, suffix: string): string => {
  if (!parentId) return "";

  let prefix = "";
  if (parentId.startsWith("audit")) {
    prefix = "Audit";
  } else if (parentId.startsWith("N") || parentId.startsWith("notice")) {
    prefix = "Notice";
  } else if (parentId.startsWith("L") || parentId.startsWith("litigation")) {
    prefix = "Litigation";
  }

  return prefix ? `${prefix} ${suffix}` : "";
};

export const getGaiaObjectPath = (
  gaiaObjectId: string,
  tabId: string
): string => {
  if (!gaiaObjectId) return "#";

  if (gaiaObjectId.startsWith("audit")) {
    return `/audit/${gaiaObjectId}?tabId=${tabId}`;
  } else if (gaiaObjectId.startsWith("N")) {
    return `/notices/${gaiaObjectId}?tabId=${tabId}`;
  } else if (gaiaObjectId.startsWith("L")) {
    return `/litigations/${gaiaObjectId}?tabId=${tabId}`;
  } else {
    console.error("Unhandled gaiaObjectId format:", gaiaObjectId);
    return "#";
  }
};

export const getGaiaObjectCategory = (gaiaObjectId: string) => {
  return gaiaObjectId?.startsWith("N")
    ? "notices"
    : gaiaObjectId?.startsWith("L")
    ? "litigations"
    : gaiaObjectId?.startsWith("a")
    ? "audit"
    : "";
};

export const getGaiaObjectCategoryName = (gaiaObjectId: string) => {
  return gaiaObjectId?.startsWith("N")
    ? "Notice"
    : gaiaObjectId?.startsWith("L")
    ? "Litigation"
    : gaiaObjectId?.startsWith("a")
    ? "Audit"
    : "";
};

export const parseType = (rawType?: string): string => {
  if (!rawType) return "";

  const lower = rawType.toLowerCase();

  if (lower === "litigation" || lower === "litigations") return "litigation";
  if (lower === "notice" || lower === "notices") return "notice";
  if (lower === "createaudit") return "createaudit";

  return "";
};

// Builds GaiaObject props structure from form data for title generation
export const buildGaiaObjectPropsFromFormData = (
  data: any,
  category: string,
  titleSuffix?: string,
  entityOptions?: any[]
) => {
  return {
    country: data.customAttributesDataMap?.country,
    governmentEntity: data.customAttributesDataMap?.governmentEntity,
    localJurisdiction: data.customAttributesDataMap?.localJurisdiction,
    entity: data.customAttributesDataMap?.entity,
    noticeType: data.customAttributesDataMap?.noticeType,
    taxPeriodStartDate: data.customAttributesDataMap?.taxPeriodStart,
    taxPeriodEndDate: data.customAttributesDataMap?.taxPeriodEnd,
    litigationType: data.customAttributesDataMap?.litigationType,
    taxTypes: data.customAttributesDataMap?.taxType,
    category,
    titleSuffix,
    entityOptions,
  };
};

// Processes form data for GaiaObject submission with title generation and custom attributes
export const processGaiaObjectForSubmission = (
  data: any,
  teamName: string,
  category: string,
  customAttributes?: any[],
  entityOptions?: any[]
) => {
  const processedData = { ...data };
  const gaiaobjectProps = buildGaiaObjectPropsFromFormData(
    data,
    category,
    data.titleSuffix,
    entityOptions
  );

  processedData.title = getGaiaobjectName(gaiaobjectProps);
  processedData.teamName = teamName;
  processedData.category = category;
  processedData.templateId = data.template;

  if (customAttributes) {
    processedData.customAttributesDataMap = generateDynamicAttributesDataMap(
      customAttributes,
      data[CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME]
    );
  }

  return processedData;
};
